// companycam — CRM side of the CompanyCam sync (Oct 1, 2026).
//
// Crews shoot in the CompanyCam app; CompanyCam keeps the full-size originals
// with GPS + capture time. The CRM mirrors project + photo metadata into
// companycam_projects / companycam_photos (edge fns companycam-sync +
// companycam-webhook) and shows CompanyCam's own image URLs — no copies.
//
// Reads are open to any active staff member (RLS get_my_role() not null) so
// the PM Board works for project_manager, which has no deals SELECT. Linking a
// project by hand goes through the companycam_link_project() RPC.

import { useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { fetchEdge, readEdgeJson } from "./edgeError";

export interface CcProject {
  id: string;
  name: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  deal_id: string | null;
  match_status: "auto" | "preferred" | "manual" | "ambiguous" | "unmatched";
  candidate_deal_ids: string[];
  photo_count: number | null;
  project_url: string | null;
  archived: boolean;
  cc_created_at: string | null;
  cc_updated_at: string | null;
  photos_synced_at: string | null;
}

export interface CcPhoto {
  id: string;
  project_id: string;
  deal_id: string | null;
  captured_at: string | null;
  lat: number | null;
  lon: number | null;
  creator_name: string | null;
  description: string | null;
  tags: string[];
  uri_thumb: string | null;
  uri_web: string | null;
  uri_original: string | null;
  photo_url: string | null;
  status: string;
}

const PROJECT_COLS = "id,name,street,city,zip,deal_id,match_status,candidate_deal_ids,photo_count,project_url,archived,cc_created_at,cc_updated_at,photos_synced_at";
const PHOTO_COLS = "id,project_id,deal_id,captured_at,lat,lon,creator_name,description,tags,uri_thumb,uri_web,uri_original,photo_url,status";

export async function fetchDealProjects(dealId: string): Promise<CcProject[]> {
  const { data, error } = await supabase.from("companycam_projects").select(PROJECT_COLS)
    .eq("deal_id", dealId).order("cc_created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as CcProject[];
}

/** Active photos for one or more deals, newest capture first. Paged past the
 *  1,000-row PostgREST cap (a big job can carry several hundred photos). */
export async function fetchDealPhotos(dealIds: string[]): Promise<CcPhoto[]> {
  if (!dealIds.length) return [];
  const out: CcPhoto[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from("companycam_photos").select(PHOTO_COLS)
      .in("deal_id", dealIds).eq("status", "active")
      .order("captured_at", { ascending: false, nullsFirst: false }).order("id")
      .range(from, from + 999);
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as CcPhoto[]));
    if (!data || data.length < 1000) break;
    if (from > 20000) break;
  }
  return out;
}

/** Projects a person may want to link to this deal: ones the address matcher
 *  listed this deal as a candidate for, plus unlinked projects on the same
 *  street number. */
export async function fetchLinkCandidates(dealId: string, street: string | null): Promise<CcProject[]> {
  const byCand = await supabase.from("companycam_projects").select(PROJECT_COLS)
    .contains("candidate_deal_ids", [dealId]).neq("deal_id", dealId);
  const num = (street ?? "").trim().match(/^\d+/)?.[0];
  const byNum = num
    ? await supabase.from("companycam_projects").select(PROJECT_COLS)
        .is("deal_id", null).ilike("street", `${num} %`).limit(25)
    : { data: [] as CcProject[], error: null };
  if (byCand.error) throw new Error(byCand.error.message);
  if (byNum.error) throw new Error(byNum.error.message);
  const seen = new Set<string>();
  return [...((byCand.data ?? []) as CcProject[]), ...((byNum.data ?? []) as CcProject[])]
    .filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
}

export async function searchProjects(q: string): Promise<CcProject[]> {
  const s = q.replace(/[%_\\,()]/g, " ").trim();
  if (s.length < 3) return [];
  const { data, error } = await supabase.from("companycam_projects").select(PROJECT_COLS)
    .or(`street.ilike.%${s}%,name.ilike.%${s}%`).order("cc_updated_at", { ascending: false }).limit(25);
  if (error) throw new Error(error.message);
  return (data ?? []) as CcProject[];
}

export async function linkProject(projectId: string, dealId: string | null): Promise<void> {
  const { error } = await supabase.rpc("companycam_link_project", { p_project_id: projectId, p_deal_id: dealId });
  if (error) throw new Error(error.message);
}

/** Pull the latest photos for a deal straight from CompanyCam (the webhook
 *  normally does this within seconds — this is the manual "Refresh"). */
export async function refreshDealPhotos(dealId: string): Promise<{ photos: number }> {
  const { data: s } = await supabase.auth.getSession();
  const res = await fetchEdge(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/companycam-sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${s.session?.access_token ?? ""}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({ mode: "deal", deal_id: dealId, with_tags: true }),
  });
  const body = await readEdgeJson(res) as { photos?: number; error?: string };
  if (!res.ok) throw new Error(body?.error || `Refresh failed (HTTP ${res.status})`);
  return { photos: Number(body?.photos) || 0 };
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const R = 6371000;
export function metersBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLon = toRad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** "on site" / "0.3 mi away" — distance from the photo's GPS to the property. */
export function distanceLabel(ph: CcPhoto, lat: number | null | undefined, lon: number | null | undefined): { text: string; far: boolean } | null {
  if (ph.lat == null || ph.lon == null || lat == null || lon == null) return null;
  const m = metersBetween(ph.lat, ph.lon, Number(lat), Number(lon));
  if (m <= 150) return { text: "on site", far: false };
  const mi = m / 1609.34;
  return { text: `${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi away`, far: true };
}

export function fmtCaptured(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function dayKey(iso: string | null): string {
  if (!iso) return "unknown";
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

/** Tag match for PM Board checklist items — case/punctuation-insensitive,
 *  either way contains (tag "Drip edge" matches item "Drip Edge installed"). */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
export function tagsMatchLabel(tags: string[] | null | undefined, label: string): boolean {
  const l = norm(label);
  if (!l || !tags?.length) return false;
  return tags.some((t) => {
    const n = norm(t);
    return n.length >= 3 && (l.includes(n) || n.includes(l));
  });
}
export function photoMatchesLabel(ph: CcPhoto, label: string): boolean {
  return tagsMatchLabel(ph.tags, label);
}

export const MATCH_LABEL: Record<CcProject["match_status"], string> = {
  auto: "Matched by address",
  preferred: "Matched (best of several deals at this address)",
  manual: "Linked by hand",
  ambiguous: "Needs review — several deals at this address",
  unmatched: "No deal found at this address",
};

/* ------------------------------------------------------------------ */
/* Job phases (Greg, Oct 1, 2026)                                      */
/* ------------------------------------------------------------------ */
// "preinspection is anything before adjuster meeting date, reinspection is
//  anything from adjuster meeting to build day, the rest of photos are build
//  day, any photos 3 weeks or later from build day are warranty photos."
//
// Compared on the ET calendar day the photo was captured. Adjuster meeting =
// adjuster_meeting_date (fake-UTC — the date part is the ET wall date). Build
// day = build_completed_date, else build_scheduled_date. Missing dates: with
// no adjuster date everything before build day is pre-inspection; with no
// build day everything from the meeting on is reinspection.

export type PhotoPhase = "pre_inspection" | "reinspection" | "build" | "warranty";

export const PHASE_META: Record<PhotoPhase, { label: string; short: string; tone: string }> = {
  pre_inspection: { label: "Pre-inspection", short: "Pre-insp", tone: "bg-sky-100 text-sky-800 border-sky-200" },
  reinspection:   { label: "Reinspection",   short: "Reinsp",   tone: "bg-purple-100 text-purple-800 border-purple-200" },
  build:          { label: "Build day",      short: "Build",    tone: "bg-orange-100 text-orange-800 border-orange-200" },
  warranty:       { label: "Warranty",       short: "Warranty", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};
export const PHASE_ORDER: PhotoPhase[] = ["pre_inspection", "reinspection", "build", "warranty"];
export const WARRANTY_AFTER_DAYS = 21;

export interface PhaseDates {
  adjuster_meeting_date?: string | null;
  build_completed_date?: string | null;
  build_scheduled_date?: string | null;
}

function addDaysISO(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function photoPhase(ph: Pick<CcPhoto, "captured_at">, deal: PhaseDates): PhotoPhase {
  const day = dayKey(ph.captured_at);
  const adj = deal.adjuster_meeting_date ? deal.adjuster_meeting_date.slice(0, 10) : null;
  const buildRaw = deal.build_completed_date ?? deal.build_scheduled_date ?? null;
  const build = buildRaw ? buildRaw.slice(0, 10) : null;
  if (day === "unknown") return adj ? "reinspection" : "pre_inspection";
  if (build && day >= addDaysISO(build, WARRANTY_AFTER_DAYS)) return "warranty";
  if (build && day >= build) return "build";
  if (adj && day >= adj) return "reinspection";
  return "pre_inspection";
}

/* ------------------------------------------------------------------ */
/* Realtime                                                            */
/* ------------------------------------------------------------------ */

/** Re-run `onChange` when photos or projects for these deals change.
 *  Filtered server-side to the deal ids (useRealtimeTable has no filter) and
 *  debounced — a sync can upsert hundreds of rows for one job in a second. */
export function useCompanyCamRealtime(dealIds: string[], onChange: () => void) {
  const cb = useRef(onChange);
  cb.current = onChange;
  const key = [...dealIds].sort().join(",");
  useEffect(() => {
    if (!key) return;
    const ids = key.split(",").slice(0, 100);
    const filter = ids.length === 1 ? `deal_id=eq.${ids[0]}` : `deal_id=in.(${ids.join(",")})`;
    let t: ReturnType<typeof setTimeout> | null = null;
    const bump = () => { if (t) clearTimeout(t); t = setTimeout(() => cb.current(), 1200); };
    const ch = supabase.channel(`cc-${key.slice(0, 40)}-${Date.now()}`)
      .on("postgres_changes" as never, { event: "*", schema: "public", table: "companycam_photos", filter }, bump)
      .on("postgres_changes" as never, { event: "*", schema: "public", table: "companycam_projects", filter }, bump)
      .subscribe();
    return () => { if (t) clearTimeout(t); supabase.removeChannel(ch); };
  }, [key]);
}
